import * as R from 'remeda';
import { clock } from './clock';

type Timer = { startTimestamp: number; metadata: unknown };

export type TimeInterval = [start: number, end: number];

const timerMap: Record<string, Timer> = {};

const hasAction = (metadata: unknown): metadata is { action: string } => {
  return (
    typeof metadata === 'object' &&
    metadata !== null &&
    'action' in metadata &&
    typeof (metadata as Record<string, unknown>)['action'] === 'string'
  );
};

export const timers = {
  isRunning: (key: string, expectedAction: string): boolean => {
    const entry = timerMap[key];
    return (
      !!entry &&
      hasAction(entry.metadata) &&
      entry.metadata.action === expectedAction
    );
  },

  snapshot: () => {
    return { ...timerMap };
  },

  clear: () => {
    Object.keys(timerMap).forEach(k => delete timerMap[k]);
  },

  start: (name: string, metadata?: unknown) => {
    if (name in timerMap) {
      // Ignore starting a timer again
      return;
    }

    timerMap[name] = { startTimestamp: clock.now(), metadata };
  },
  setMetadata: (name: string, metadata?: unknown) => {
    if (!(name in timerMap)) {
      return;
    }

    const entry = timerMap[name];
    if (entry) {
      entry.metadata = metadata;
    }
  },
  updateMetadata: <TMetadata>(
    name: string,
    updateFn: (metadata: TMetadata) => TMetadata
  ) => {
    const entry = timerMap[name];
    if (!entry?.metadata) {
      return;
    }

    entry.metadata = updateFn(entry.metadata as TMetadata);
  },
  elapsedInterval: <TMetadata>(name: string) => {
    const entry = timerMap[name];
    if (!entry) {
      return undefined;
    }

    return {
      interval: [entry.startTimestamp, clock.now()] as TimeInterval,
      metadata: entry.metadata as TMetadata,
    };
  },
  stop: <TMetadata>(name: string) => {
    if (!(name in timerMap)) {
      return undefined;
    }

    const result = timers.elapsedInterval<TMetadata>(name);
    delete timerMap[name];
    return result;
  },

  minStartTimestamp: (): number | undefined => {
    const values = Object.values(timerMap);

    if (values.length > 0) {
      return R.firstBy(values, val => val.startTimestamp)!.startTimestamp;
    }

    return undefined;
  },
};
